/**
 * B090060 在庫データEXCEL取込
 */

import { Box } from "@mui/material";
import {
  B090060ImportLocationido,
  B090060ImportSokobuturyu,
  B090060ImportSokohonsha,
  B090060ImportZaikochosei,
  B090060LocationidoRequest,
  B090060SokobuturyuRequest,
  B090060SokohonshaRequest,
  B090060ZaikochoseiRequest,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SFileField,
  SFrame,
  SLabel,
  SNormalButton,
  SText,
  STextArea,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import { labels } from "messages/labels";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { outputText } from "services";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  pageId: string;
  idouYmd: string;
  shainCd: string;
  bumonCd: string;
  haraidashisokoCd: string;
  ukeiresokoCd: string;
  haraidashilocationCd: string;
  choseiYmd: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  pageId: string;
  idouYmd: string;
  shainCd: string;
  bumonCd: string;
  haraidashisokoCd: string;
  ukeiresokoCd: string;
  haraidashilocationCd: string;
  choseiYmd: string;
  torikomidatashubetsu: string;
  fileUploadArea: File;
  result: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  pageId: "",
  idouYmd: "",
  shainCd: "",
  bumonCd: "",
  haraidashisokoCd: "",
  ukeiresokoCd: "",
  haraidashilocationCd: "",
  choseiYmd: "",
  torikomidatashubetsu: "",
  fileUploadArea: undefined,
  result: "",
};

const focusId = "B090060";

// B090060 在庫データEXCEL取込
export const B090060 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 実行ボタン活性制御
  const disabledExecuteButton = methods.watch("fileUploadArea") === undefined;
  // 結果レポート出力ボタン活性制御
  const [isDisabledReportButton, setIsDesabledReportButton] = useState(true);
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 初期表示イベント
  useEffect(() => {
    // 処理詳細１
    // pageIdセット
    if (props.pageId) {
      methods.setValue("pageId", props.pageId);
    }
    // 移動日セット
    if (props.idouYmd) {
      methods.setValue("idouYmd", props.idouYmd);
    }
    // 担当者コードセット
    if (props.shainCd) {
      methods.setValue("shainCd", props.shainCd);
    }
    // 部門コードセット
    if (props.bumonCd) {
      methods.setValue("bumonCd", props.bumonCd);
    }
    // 払出倉庫コードセット
    if (props.haraidashisokoCd) {
      methods.setValue("haraidashisokoCd", props.haraidashisokoCd);
    }
    // 受入倉庫コードセット
    if (props.ukeiresokoCd) {
      methods.setValue("ukeiresokoCd", props.ukeiresokoCd);
    }
    // 払出ロケーションコードセット
    if (props.haraidashilocationCd) {
      methods.setValue("haraidashilocationCd", props.haraidashilocationCd);
    }
    // 調整日セット
    if (props.bumonCd) {
      methods.setValue("choseiYmd", props.choseiYmd);
    }

    // 取込データ種別設定
    const fields = methods.getValues();
    // B090020 からの遷移
    if (fields.pageId === "B090020") {
      methods.setValue("torikomidatashubetsu", "倉庫移動");
    }
    // B090021 からの遷移
    else if (fields.pageId === "B090021") {
      methods.setValue("torikomidatashubetsu", "倉庫移動");
    }
    // B090030 からの遷移
    else if (fields.pageId === "B090030") {
      methods.setValue("torikomidatashubetsu", "ロケーション移動");
    }
    // B090040 からの遷移
    else if (fields.pageId === "B090040") {
      methods.setValue("torikomidatashubetsu", "在庫調整");
    }
    // 初回フォーカス先
    focus(1);
  });

  // 実行ボタン押下イベント
  const handleExecute = () => {
    const fields = methods.getValues();

    methods.setValue("result", getClientMessage("B090060_001"));

    // B090020 からの遷移
    if (fields.pageId === "B090020") {
      handleSokoidoHonsha(fields);
    }
    // B090021 からの遷移
    else if (fields.pageId === "B090021") {
      handleSokoidoButsuryu(fields);
    }
    // B090030 からの遷移
    else if (fields.pageId === "B090030") {
      handleLocationido(fields);
    }
    // B090040 からの遷移
    else if (fields.pageId === "B090040") {
      handleZaikochosei(fields);
    }

    setIsDesabledReportButton(false);
  };

  // 倉庫移動入力(本社業務)
  const handleSokoidoHonsha = (fields: Fields) => {
    const param: B090060SokohonshaRequest = {
      excelFileData: fields.fileUploadArea!,
      idoYmd: fields.idouYmd!,
      shainCd: fields.shainCd!,
      bumonCd: fields.bumonCd!,
      haraisokoCd: fields.haraidashisokoCd!,
      ukesokoCd: fields.ukeiresokoCd!,
    };

    B090060ImportSokohonsha(param)
      .then((value) => {
        methods.setValue("result", value.data.torikomikeka);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 倉庫移動入力(物流業務)
  const handleSokoidoButsuryu = (fields: Fields) => {
    const param: B090060SokobuturyuRequest = {
      excelFileData: fields.fileUploadArea!,
      idoYmd: fields.idouYmd!,
      shainCd: fields.shainCd!,
      bumonCd: fields.bumonCd!,
      harailocationCd: fields.haraidashilocationCd!,
      ukesokoCd: fields.ukeiresokoCd!,
    };

    B090060ImportSokobuturyu(param)
      .then((value) => {
        methods.setValue("result", value.data.torikomikeka);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // ロケーション移動入力
  const handleLocationido = (fields: Fields) => {
    const param: B090060LocationidoRequest = {
      excelFileData: fields.fileUploadArea!,
      idoYmd: fields.idouYmd!,
      shainCd: fields.shainCd!,
      bumonCd: fields.bumonCd!,
    };

    B090060ImportLocationido(param)
      .then((value) => {
        methods.setValue("result", value.data.torikomikeka);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 在庫調整入力
  const handleZaikochosei = (fields: Fields) => {
    const param: B090060ZaikochoseiRequest = {
      excelFileData: fields.fileUploadArea!,
      choseiYmd: fields.choseiYmd!,
      shainCd: fields.shainCd!,
      bumonCd: fields.bumonCd!,
    };

    B090060ImportZaikochosei(param)
      .then((value) => {
        methods.setValue("result", value.data.torikomikeka);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  const handleClickReportButton = () => {
    const text = methods.getValues().result;
    if (typeof text === "undefined") return;
    outputText(text, "在庫データEXCEL取込結果.txt");
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="在庫データEXCEL取込"
        gridTemplateColumns="7rem 10rem 13rem "
      >
        <SLabel>取込データ種別</SLabel>
        <STextField
          label="取込データ種別"
          name="torikomidatashubetsu"
          disabled
        />
        <Box />
      </SFrame>

      <SFrame gridTemplateColumns="30rem">
        <SFileField
          name="fileUploadArea"
          label="取り込む在庫データファイル"
          type="excel"
          height="7rem"
        />

        <SNormalButton
          label={labels.buttons.execute}
          onClick={handleExecute}
          disabled={disabledExecuteButton}
          align="right"
          {...register(1)}
        />
      </SFrame>

      <SFrame>
        <SText align="left">在庫データEXCEL取込結果</SText>

        <STextArea name="result" height="7rem" disabled />

        <SNormalButton
          label={labels.buttons.resultReport}
          onClick={handleClickReportButton}
          disabled={isDisabledReportButton}
          align="right"
          {...register(2)}
        />
      </SFrame>
    </FormProvider>
  );
};
